import type { Box2d, Editor, TLShapeId } from '@digitalsamba/tldraw';
import { CONTENT_BOARD_BG_SHAPE_ID } from 'features/content-whiteboard/constants';
import { getTldrawSync } from 'utils/whiteboard/tldrawRuntime';

const SCALE = 2;

type CanvasWithContext = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
};

const createScaledCanvas = (bounds: Box2d): CanvasWithContext | null => {
  const canvas = document.createElement('canvas');
  canvas.width = bounds.w * SCALE;
  canvas.height = bounds.h * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  ctx.scale(SCALE, SCALE);
  return { canvas, ctx };
};

const convertSvgToPng = async (
  svg: SVGSVGElement | null,
  bounds: Box2d,
  minX: number,
  minY: number
): Promise<HTMLCanvasElement | null> => {
  if (!svg) {
    return null;
  }

  const svgData = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  img.src = url;

  return new Promise((resolve) => {
    img.onload = () => {
      const created = createScaledCanvas(bounds);
      if (!created) {
        URL.revokeObjectURL(url);
        resolve(null);
        return;
      }
      const { canvas, ctx } = created;
      ctx.drawImage(
        img,
        bounds.x + Math.abs(minX),
        bounds.y + Math.abs(minY),
        bounds.w,
        bounds.h,
        0,
        0,
        bounds.w,
        bounds.h
      );
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
  });
};

const createBackgroundCanvas = async (
  imgUrl: string | null,
  bounds: Box2d
): Promise<HTMLCanvasElement | null> => {
  if (!imgUrl) {
    return null;
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imgUrl;
    img.onload = () => {
      const created = createScaledCanvas(bounds);
      if (!created) {
        resolve(null);
        return;
      }
      const { canvas, ctx } = created;
      ctx.drawImage(img, 0, 0, bounds.w, bounds.h);
      resolve(canvas);
    };
    img.onerror = () => resolve(null);
  });
};

const getBackgroundImageUrl = (shape: any): string | null =>
  shape.props?.url ?? shape.meta?.src ?? null;

export type ExportContentWhiteboardParams = {
  editor: Editor;
};

export const exportContentWhiteboard = async ({
  editor,
}: ExportContentWhiteboardParams): Promise<void> => {
  const bgShape = editor.getShape(CONTENT_BOARD_BG_SHAPE_ID);
  if (!bgShape) {
    return;
  }

  const base = editor.getShapePageBounds(CONTENT_BOARD_BG_SHAPE_ID);
  if (!base) {
    return;
  }

  const { Box2d } = getTldrawSync();
  const bounds = new Box2d(base.x, base.y, base.w, base.h);

  let minX = 0;
  let minY = 0;

  const toExport: TLShapeId[] = [];
  for (const id of editor.getPageShapeIds(editor.currentPageId)) {
    const b = editor.getShapePageBounds(id);
    if (b) {
      minY = Math.min(minY, b.y);
      minX = Math.min(minX, b.x);
      if (b.collides(bounds)) {
        toExport.push(id);
      }
    }
  }

  let shapesCanvas: HTMLCanvasElement | null = null;
  if (toExport.length > 0) {
    const svg = await editor.getSvg(toExport, {
      background: false,
      padding: 0,
      scale: 1,
    });
    if (svg instanceof SVGSVGElement) {
      shapesCanvas = await convertSvgToPng(svg, bounds, minX, minY);
    }
  }

  const bgUrl = getBackgroundImageUrl(bgShape);
  const bgCanvas = await createBackgroundCanvas(bgUrl, bounds);

  if (!bgCanvas && !shapesCanvas) {
    return;
  }

  const created = createScaledCanvas(bounds);
  if (!created) {
    return;
  }
  const { canvas: finalCanvas, ctx: finalCtx } = created;

  if (bgCanvas) {
    finalCtx.drawImage(bgCanvas, 0, 0, bounds.w, bounds.h);
  }

  if (shapesCanvas) {
    finalCtx.drawImage(shapesCanvas, 0, 0, bounds.w, bounds.h);
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    finalCanvas.toBlob(resolve);
  });

  if (!blob) {
    return;
  }

  const link = document.createElement('a');
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  link.download = 'export.png';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};
